// Library Methods
import { useTranslation } from "react-i18next";
import React from "react";

// Components
import { Box, Divider, Grid, Typography } from "@mui/material";
import ExamCardButton from "../ui/ExamCardButton";
import ExamCardToggles from "../ui/ExamCardToggles";
import WhichEyeSection from "../ui/WhichEyeSection";
import TitleWithTooltip from "../../../../../components/UI/TitleWithTooltip";
import ExamCardContainer from "../ui/ExamCardContainer";
import ExamCardHeader from "../ui/ExamCardHeader";

const ContrastSensitivityCard = ({
  label,
  examAlgorithm,
  selectedExamCards,
  updateExamCardProperty,
  onChangeEye,
  onChangeSectionOrder,
  onToggleCard,
}) => {
  // internationalization
  const { t } = useTranslation();

  const defaultContrastSensitivity = {
    examFieldType: examAlgorithm,
    examType: "PELLI_ROBSON",
    eyeCheckboxes: { right: true, left: true },
    binocular: true,
    sectionOrder: { right: 0, left: 1 },
    optionalToggles: {
      requiresTutorial: true,
    },
  };

  const selectedExamCard = selectedExamCards.find(
    (examCard) => examCard.examFieldType === examAlgorithm
  );
  const examCardInfo = selectedExamCard ?? defaultContrastSensitivity;
  const isActive = Boolean(selectedExamCard);

  const { examType, eyeCheckboxes, sectionOrder, optionalToggles, binocular } =
    examCardInfo;

  const textStyle = {
    color: isActive ? "#FFF" : "#5B6D7A",
    fontSize: "14px",
  };

  return (
    <ExamCardContainer isActive={isActive}>
      <ExamCardHeader
        label={label}
        isActive={isActive}
        onChange={(e) => {
          onToggleCard(e, defaultContrastSensitivity);
        }}
      />
      <Grid item container display="flex" flexDirection="column" gap="8px">
        {/* contrast sensitivity type */}
        <Grid
          item
          display="flex"
          flexDirection="column"
          gap="8px"
          height="64.5px"
        >
          <TitleWithTooltip
            title={t("exams_table_type")}
            tooltip={t("more_available_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={examType === "PELLI_ROBSON"}
                updateValue={() =>
                  updateExamCardProperty("examType", "PELLI_ROBSON")
                }
                label="Pelli Robson"
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={examType === "TUMBLING_E"}
                updateValue={() =>
                  updateExamCardProperty("examType", "TUMBLING_E")
                }
                label="Tumbling E"
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* eye */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_eye")}
            tooltip={t("which_eye_test_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={!binocular}
                updateValue={() => updateExamCardProperty("binocular", false)}
                label={t("word_monocular")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={binocular}
                updateValue={() => updateExamCardProperty("binocular", true)}
                label={t("word_binocular")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>
        </Grid>

        <WhichEyeSection
          isActive={isActive}
          isRightFirst={sectionOrder.right === 0}
          eyeCheckboxes={eyeCheckboxes}
          onChangeSectionOrder={onChangeSectionOrder}
          onChangeEye={onChangeEye}
          isHidden={binocular}
        />

        <Box height={{ xs: "0px", sm: "343px" }} />

        <Divider color="#5B6D7A" />

        <Grid display="flex" flexDirection="column">
          <ExamCardToggles
            isActive={isActive}
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={defaultContrastSensitivity.optionalToggles}
          />
        </Grid>

        <Grid item textAlign="center">
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("new_test")}
          </Typography>
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("try_it_out")}
          </Typography>
        </Grid>
      </Grid>
    </ExamCardContainer>
  );
};

export default ContrastSensitivityCard;
